import React from 'react';

import isValid from 'date-fns/isValid';
import { FormattedMessage } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import Lozenge from '@atlaskit/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { CalendarXSIcon } from '../../../../../../../common/ui/calendar-xs-icon';

import type { searchMercuryFocusAreaStatusTargetDate_AtlassianSearchPage$key } from './__generated__/searchMercuryFocusAreaStatusTargetDate_AtlassianSearchPage.graphql';
import { messages } from './messages';
import { FocusAreaTargetDate, getDisplayedFocusAreaStatus } from './utils';

interface SearchMercuryFocusAreaStatusTargetDateProps {
	node: searchMercuryFocusAreaStatusTargetDate_AtlassianSearchPage$key;
}

export const SearchMercuryFocusAreaStatusTargetDate = ({
	node,
}: SearchMercuryFocusAreaStatusTargetDateProps): React.JSX.Element | null => {
	const data = useFragment(
		graphql`
			fragment searchMercuryFocusAreaStatusTargetDate_AtlassianSearchPage on SearchResult {
				... on SearchResultMercuryFocusArea {
					focusArea {
						status {
							key
							displayName
						}
						health {
							key
							color
							displayName
						}
						targetDate {
							targetDate
							targetDateType
						}
					}
				}
			}
		`,
		node,
	);

	const { displayedStatus, displayedStatusAppearance } = getDisplayedFocusAreaStatus({
		healthColor: data.focusArea?.health?.color,
		statusKey: data.focusArea?.status?.key,
		statusDisplayName: data.focusArea?.status?.displayName,
		healthDisplayName: data.focusArea?.health?.displayName,
		healthKey: data.focusArea?.health?.key,
	});

	if (!displayedStatus) {
		return null;
	}

	const targetDate = data.focusArea?.targetDate?.targetDate;
	const targetDateType = data.focusArea?.targetDate?.targetDateType;
	const displayedTargetDate = targetDate && targetDateType && isValid(new Date(targetDate)) && (
		<FocusAreaTargetDate targetDate={targetDate} targetDateType={targetDateType} />
	);
	return (
		<Box xcss={statusAndTargetDateXcss} testId="search-mercury-focus-area-status-target-date">
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
			<Lozenge appearance={displayedStatusAppearance}>{displayedStatus}</Lozenge>
			{displayedTargetDate && (
				<>
					{' '}
					<Box xcss={targetDateForXcss}>
						<FormattedMessage {...messages.for} />
					</Box>
					<Lozenge>
						<Inline alignBlock="center" space="space.050">
							<CalendarXSIcon /> {displayedTargetDate}
						</Inline>
					</Lozenge>
				</>
			)}
		</Box>
	);
};

const statusAndTargetDateXcss = xcss({ display: 'flex', alignItems: 'center', gap: 'space.100' });
const targetDateForXcss = xcss({ font: token('font.body.small'), color: 'color.text.subtlest' });
